import type { ResourceReleaseReason } from "@afar-research/shared/resourceLedger";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type WorkspaceSurfacePersistence = "none" | "session" | "device";
export type WorkspaceSurfaceLifetime = "active" | "while-open" | "retained";
export type WorkspaceSurfacePresentation = "inline" | "sheet";

export interface WorkspaceSurfaceWorkspaceIdentity {
  readonly environmentId: string;
  readonly projectId: string;
}

export type WorkspaceSurfaceScope =
  | { readonly type: "workspace"; readonly workspace: WorkspaceSurfaceWorkspaceIdentity }
  | {
      readonly type: "thread";
      readonly workspace: WorkspaceSurfaceWorkspaceIdentity;
      readonly threadId: string;
    };

export interface WorkspaceSurfaceDescriptor<Kind extends string = string> {
  readonly id: string;
  readonly kind: Kind;
}

export interface WorkspaceSurfaceState<Descriptor extends WorkspaceSurfaceDescriptor> {
  readonly isOpen: boolean;
  readonly activeSurfaceId: string | null;
  readonly surfaces: Descriptor[];
}

export type WorkspaceSurfaceCommand<Descriptor extends WorkspaceSurfaceDescriptor> =
  | { readonly type: "open"; readonly descriptor: Descriptor; readonly activate?: boolean }
  | { readonly type: "activate"; readonly surfaceId: string }
  | { readonly type: "close"; readonly surfaceId: string }
  | { readonly type: "close-others"; readonly surfaceId: string }
  | { readonly type: "close-to-right"; readonly surfaceId: string }
  | { readonly type: "close-all" }
  | { readonly type: "show" }
  | { readonly type: "hide" }
  | { readonly type: "toggle-visibility" };

export function dispatchWorkspaceSurfaceState<Descriptor extends WorkspaceSurfaceDescriptor>(
  state: WorkspaceSurfaceState<Descriptor>,
  command: WorkspaceSurfaceCommand<Descriptor>,
): WorkspaceSurfaceState<Descriptor> {
  switch (command.type) {
    case "open": {
      const surfaces = state.surfaces.some((surface) => surface.id === command.descriptor.id)
        ? state.surfaces.map((surface) =>
            surface.id === command.descriptor.id ? command.descriptor : surface,
          )
        : [...state.surfaces, command.descriptor];
      return {
        isOpen: true,
        activeSurfaceId: command.activate === false ? state.activeSurfaceId : command.descriptor.id,
        surfaces,
      };
    }
    case "activate":
      return state.surfaces.some((surface) => surface.id === command.surfaceId)
        ? { ...state, isOpen: true, activeSurfaceId: command.surfaceId }
        : state;
    case "close": {
      const index = state.surfaces.findIndex((surface) => surface.id === command.surfaceId);
      if (index < 0) return state;
      const surfaces = state.surfaces.filter((surface) => surface.id !== command.surfaceId);
      if (state.activeSurfaceId !== command.surfaceId) {
        return { ...state, isOpen: surfaces.length > 0 && state.isOpen, surfaces };
      }
      const fallback = surfaces[Math.min(index, surfaces.length - 1)] ?? null;
      return {
        ...state,
        isOpen: surfaces.length > 0 && state.isOpen,
        activeSurfaceId: fallback?.id ?? null,
        surfaces,
      };
    }
    case "close-others": {
      const surface = state.surfaces.find((candidate) => candidate.id === command.surfaceId);
      return !surface || state.surfaces.length === 1
        ? state
        : { isOpen: true, activeSurfaceId: surface.id, surfaces: [surface] };
    }
    case "close-to-right": {
      const index = state.surfaces.findIndex((surface) => surface.id === command.surfaceId);
      if (index < 0 || index === state.surfaces.length - 1) return state;
      const surfaces = state.surfaces.slice(0, index + 1);
      const activeSurfaceId = surfaces.some((surface) => surface.id === state.activeSurfaceId)
        ? state.activeSurfaceId
        : command.surfaceId;
      return { ...state, activeSurfaceId, surfaces };
    }
    case "close-all":
      return state.surfaces.length === 0
        ? state
        : { isOpen: false, activeSurfaceId: null, surfaces: [] };
    case "show":
      return state.isOpen ? state : { ...state, isOpen: true };
    case "hide":
      return state.isOpen ? { ...state, isOpen: false } : state;
    case "toggle-visibility":
      return { ...state, isOpen: !state.isOpen };
  }
}

export interface WorkspaceSurfaceTransition<Descriptor extends WorkspaceSurfaceDescriptor> {
  readonly state: WorkspaceSurfaceState<Descriptor>;
  readonly removedDescriptors: readonly Descriptor[];
}

/**
 * Apply one descriptor command and expose the descriptors it removed.
 *
 * Consumers with feature-owned close effects use this single transition instead
 * of reimplementing close, close-others, and close-to-right selection rules.
 */
export function transitionWorkspaceSurfaceState<Descriptor extends WorkspaceSurfaceDescriptor>(
  state: WorkspaceSurfaceState<Descriptor>,
  command: WorkspaceSurfaceCommand<Descriptor>,
): WorkspaceSurfaceTransition<Descriptor> {
  const nextState = dispatchWorkspaceSurfaceState(state, command);
  if (nextState.surfaces === state.surfaces) {
    return { state: nextState, removedDescriptors: [] };
  }
  const retainedIds = new Set(nextState.surfaces.map((descriptor) => descriptor.id));
  return {
    state: nextState,
    removedDescriptors: state.surfaces.filter((descriptor) => !retainedIds.has(descriptor.id)),
  };
}

export interface WorkspaceSurfaceLease {
  readonly release: (reason: ResourceReleaseReason) => void;
}

export type WorkspaceSurfaceFailure =
  | {
      readonly type: "duplicate-definition";
      readonly kind: string;
    }
  | {
      readonly type: "duplicate-descriptor";
      readonly surfaceId: string;
    }
  | {
      readonly type: "unknown-kind";
      readonly kind: string;
      readonly surfaceId: string;
    }
  | {
      readonly type: "scope-mismatch";
      readonly expectedScope: "workspace" | "thread";
      readonly actualScope: WorkspaceSurfaceScope["type"];
      readonly surfaceId: string;
    }
  | {
      readonly type: "persistence-policy-mismatch";
      readonly expectedPersistence: WorkspaceSurfacePersistence;
      readonly actualPersistence: Exclude<WorkspaceSurfacePersistence, "none">;
      readonly surfaceId: string;
    }
  | {
      readonly type: "descriptor-decode-failed";
      readonly kind: string;
      readonly identityVersion: number;
      readonly surfaceId: string;
    }
  | {
      readonly type: "migration-failed";
      readonly kind: string;
      readonly fromVersion: number;
      readonly toVersion: number;
      readonly surfaceId: string;
      readonly cause?: unknown;
    }
  | {
      readonly type: "acquisition-failed";
      readonly kind: string;
      readonly surfaceId: string;
      readonly resourceIdentity: string;
      readonly scope: WorkspaceSurfaceScope;
      readonly generation: number;
      readonly cause: unknown;
    }
  | {
      readonly type: "release-failed";
      readonly kind: string;
      readonly surfaceId: string;
      readonly reason: ResourceReleaseReason;
      readonly cause: unknown;
    };

export interface WorkspaceSurfaceRenderInput<
  Descriptor extends WorkspaceSurfaceDescriptor = WorkspaceSurfaceDescriptor,
> {
  readonly descriptor: Descriptor;
  readonly presentation: WorkspaceSurfacePresentation;
  readonly renderRegion: (content?: ReactNode) => ReactNode;
  readonly scope: WorkspaceSurfaceScope;
}

export interface WorkspaceSurfaceAcquireInput<
  Descriptor extends WorkspaceSurfaceDescriptor = WorkspaceSurfaceDescriptor,
> {
  readonly descriptor: Descriptor;
  readonly generation: number;
  readonly scope: WorkspaceSurfaceScope;
  readonly signal: AbortSignal;
}

export interface WorkspaceSurfaceMigrationInput {
  readonly descriptor: WorkspaceSurfaceDescriptor;
  readonly fromVersion: number;
}

export interface WorkspaceSurfaceDefinition<
  Descriptor extends WorkspaceSurfaceDescriptor = WorkspaceSurfaceDescriptor,
> {
  readonly kind: Descriptor["kind"];
  readonly scope: WorkspaceSurfaceScope["type"];
  readonly identityVersion: number;
  readonly persistence: WorkspaceSurfacePersistence;
  readonly lifetime: WorkspaceSurfaceLifetime;
  readonly retentionLimit?: number;
  readonly decode: (value: unknown) => Descriptor | null;
  readonly resourceIdentity?: (descriptor: Descriptor) => string;
  readonly acquire?: (
    input: WorkspaceSurfaceAcquireInput<Descriptor>,
  ) => WorkspaceSurfaceLease | Promise<WorkspaceSurfaceLease>;
  readonly migrate?: (input: WorkspaceSurfaceMigrationInput) => Descriptor | null;
  readonly render: (input: WorkspaceSurfaceRenderInput<Descriptor>) => ReactNode;
}

export type PersistedWorkspaceSurfaceDescriptor<
  Descriptor extends WorkspaceSurfaceDescriptor = WorkspaceSurfaceDescriptor,
> = Descriptor & {
  readonly kind: string;
  readonly identityVersion: number;
  readonly descriptor: Descriptor;
};

type ReportFailure = (failure: WorkspaceSurfaceFailure) => void;

function definitionMap<Descriptor extends WorkspaceSurfaceDescriptor>(
  definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[],
  reportFailure: ReportFailure,
): ReadonlyMap<string, WorkspaceSurfaceDefinition<Descriptor>> {
  const byKind = new Map<string, WorkspaceSurfaceDefinition<Descriptor>>();
  for (const definition of definitions) {
    if (byKind.has(definition.kind)) {
      reportFailure({ type: "duplicate-definition", kind: definition.kind });
      continue;
    }
    byKind.set(definition.kind, definition);
  }
  return byKind;
}

function recordDescriptor(value: unknown): WorkspaceSurfaceDescriptor | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as { readonly id?: unknown; readonly kind?: unknown };
  return typeof candidate.id === "string" && typeof candidate.kind === "string"
    ? (candidate as WorkspaceSurfaceDescriptor)
    : null;
}

function descriptorIdentity<Descriptor extends WorkspaceSurfaceDescriptor>(
  definition: WorkspaceSurfaceDefinition<Descriptor>,
  descriptor: Descriptor,
): string {
  return definition.resourceIdentity?.(descriptor) ?? JSON.stringify(descriptor);
}

export function workspaceSurfaceScopeIdentity(scope: WorkspaceSurfaceScope): string {
  return JSON.stringify(scope);
}

export function workspaceSurfaceResourceOwnerIdentity(
  scope: WorkspaceSurfaceScope,
  descriptor: WorkspaceSurfaceDescriptor,
): string {
  return JSON.stringify({ scope, surface: { kind: descriptor.kind, id: descriptor.id } });
}

function descriptorKey(descriptor: WorkspaceSurfaceDescriptor): string {
  return `${descriptor.kind}\u0000${descriptor.id}`;
}

function selectWorkspaceSurfacePersistenceEntries<Descriptor extends WorkspaceSurfaceDescriptor>(
  definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[],
  descriptors: readonly Descriptor[],
  persistence: Exclude<WorkspaceSurfacePersistence, "none">,
  onFailure: ReportFailure = () => undefined,
): readonly {
  readonly definition: WorkspaceSurfaceDefinition<Descriptor>;
  readonly descriptor: Descriptor;
}[] {
  const byKind = definitionMap(definitions, onFailure);
  return descriptors.flatMap((descriptor) => {
    const definition = byKind.get(descriptor.kind);
    if (!definition || definition.persistence !== persistence) return [];
    return [{ definition, descriptor }];
  });
}

export function serializeWorkspaceSurfaceDescriptors<Descriptor extends WorkspaceSurfaceDescriptor>(
  definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[],
  descriptors: readonly Descriptor[],
  persistence: Exclude<WorkspaceSurfacePersistence, "none">,
  onFailure: ReportFailure = () => undefined,
): readonly PersistedWorkspaceSurfaceDescriptor<Descriptor>[] {
  return selectWorkspaceSurfacePersistenceEntries(
    definitions,
    descriptors,
    persistence,
    onFailure,
  ).map(({ definition, descriptor }) => ({
    ...descriptor,
    kind: definition.kind,
    identityVersion: definition.identityVersion,
    descriptor: { ...descriptor },
  }));
}

export function selectWorkspaceSurfaceDescriptorsForPersistence<
  Descriptor extends WorkspaceSurfaceDescriptor,
>(
  definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[],
  descriptors: readonly Descriptor[],
  persistence: Exclude<WorkspaceSurfacePersistence, "none">,
): readonly Descriptor[] {
  return selectWorkspaceSurfacePersistenceEntries(definitions, descriptors, persistence).map(
    ({ descriptor }) => descriptor,
  );
}

export function restoreWorkspaceSurfaceDescriptors<Descriptor extends WorkspaceSurfaceDescriptor>(
  definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[],
  persisted: readonly PersistedWorkspaceSurfaceDescriptor[],
  persistence: Exclude<WorkspaceSurfacePersistence, "none">,
  onFailure: ReportFailure = () => undefined,
): readonly Descriptor[] {
  const byKind = definitionMap(definitions, onFailure);
  const restored: Descriptor[] = [];

  for (const entry of persisted) {
    const descriptor = recordDescriptor(entry.descriptor);
    const surfaceId = typeof descriptor?.id === "string" ? descriptor.id : "unknown";
    const definition = byKind.get(entry.kind);
    if (!definition) {
      onFailure({ type: "unknown-kind", kind: entry.kind, surfaceId });
      continue;
    }
    if (definition.persistence !== persistence) {
      onFailure({
        type: "persistence-policy-mismatch",
        expectedPersistence: definition.persistence,
        actualPersistence: persistence,
        surfaceId,
      });
      continue;
    }
    if (!descriptor || descriptor.kind !== entry.kind) {
      onFailure({
        type: "migration-failed",
        kind: entry.kind,
        fromVersion: entry.identityVersion,
        toVersion: definition.identityVersion,
        surfaceId,
      });
      continue;
    }
    if (entry.identityVersion === definition.identityVersion) {
      const decoded = definition.decode(entry.descriptor);
      if (decoded) {
        restored.push(decoded);
        continue;
      }
      onFailure({
        type: "descriptor-decode-failed",
        kind: entry.kind,
        identityVersion: entry.identityVersion,
        surfaceId,
      });
      continue;
    }
    try {
      const migrated = definition.migrate?.({
        descriptor,
        fromVersion: entry.identityVersion,
      });
      const decoded = migrated ? definition.decode(migrated) : null;
      if (decoded) {
        restored.push(decoded);
        continue;
      }
      onFailure({
        type: "migration-failed",
        kind: entry.kind,
        fromVersion: entry.identityVersion,
        toVersion: definition.identityVersion,
        surfaceId,
      });
    } catch (cause) {
      onFailure({
        type: "migration-failed",
        kind: entry.kind,
        fromVersion: entry.identityVersion,
        toVersion: definition.identityVersion,
        surfaceId,
        cause,
      });
    }
  }

  return restored;
}

interface SurfaceLifecycleEntry<Descriptor extends WorkspaceSurfaceDescriptor> {
  readonly key: string;
  readonly definition: WorkspaceSurfaceDefinition<Descriptor>;
  readonly descriptor: Descriptor;
  readonly identity: string;
  readonly generation: number;
  readonly controller: AbortController;
  lease: WorkspaceSurfaceLease | null;
  releaseReason: ResourceReleaseReason | null;
  state: "pending" | "acquired" | "failed" | "released";
  lastActiveSequence: number;
}

export interface WorkspaceSurfaceLifecycle {
  readonly reconcile: (input: {
    readonly descriptors: readonly WorkspaceSurfaceDescriptor[];
    readonly activeSurfaceId: string | null;
    readonly open: boolean;
    readonly scope: WorkspaceSurfaceScope;
  }) => Promise<void>;
  readonly dispose: () => void;
}

export function createWorkspaceSurfaceLifecycle<
  Descriptor extends WorkspaceSurfaceDescriptor,
>(input: {
  readonly definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[];
  readonly globalRetentionLimit?: number;
  readonly onFailure?: ReportFailure;
  readonly onAcquired?: (input: {
    readonly kind: string;
    readonly surfaceId: string;
    readonly resourceIdentity: string;
    readonly scope: WorkspaceSurfaceScope;
  }) => void;
}): WorkspaceSurfaceLifecycle {
  const reportFailure = input.onFailure ?? (() => undefined);
  const definitions = definitionMap(input.definitions, reportFailure);
  const entries = new Map<string, SurfaceLifecycleEntry<Descriptor>>();
  const evictedIdentities = new Map<string, string>();
  let currentScopeIdentity: string | null = null;
  let nextGeneration = 0;
  let nextActiveSequence = 0;
  let disposed = false;
  let currentActiveSurfaceId: string | null = null;

  const releaseEntry = (
    entry: SurfaceLifecycleEntry<Descriptor>,
    reason: ResourceReleaseReason,
  ): void => {
    if (entry.state === "released") return;
    entry.releaseReason ??= reason;
    if (reason === "eviction") evictedIdentities.set(entry.key, entry.identity);
    else evictedIdentities.delete(entry.key);
    entry.controller.abort(reason);
    if (entries.get(entry.key) === entry) entries.delete(entry.key);
    if (!entry.lease) {
      if (entry.state !== "pending") entry.state = "released";
      return;
    }
    entry.state = "released";
    try {
      entry.lease.release(entry.releaseReason);
    } catch (cause) {
      reportFailure({
        type: "release-failed",
        kind: entry.descriptor.kind,
        surfaceId: entry.descriptor.id,
        reason: entry.releaseReason,
        cause,
      });
    }
  };

  const enforceRetention = (): void => {
    const retained = [...entries.values()].filter(
      (entry) =>
        entry.definition.lifetime === "retained" &&
        (entry.state === "pending" || entry.state === "acquired"),
    );
    const newestFirst = retained.toSorted((left, right) => {
      const leftActive = left.descriptor.id === currentActiveSurfaceId ? 1 : 0;
      const rightActive = right.descriptor.id === currentActiveSurfaceId ? 1 : 0;
      return rightActive - leftActive || right.lastActiveSequence - left.lastActiveSequence;
    });
    const retainedByKind = new Map<string, number>();
    let retainedGlobally = 0;
    const globalLimit = Math.max(0, input.globalRetentionLimit ?? Number.POSITIVE_INFINITY);

    for (const entry of newestFirst) {
      const kindCount = retainedByKind.get(entry.descriptor.kind) ?? 0;
      const kindLimit = Math.max(0, entry.definition.retentionLimit ?? Number.POSITIVE_INFINITY);
      const active = entry.descriptor.id === currentActiveSurfaceId;
      if (!active && (kindCount >= kindLimit || retainedGlobally >= globalLimit)) {
        releaseEntry(entry, "eviction");
        continue;
      }
      retainedByKind.set(entry.descriptor.kind, kindCount + 1);
      retainedGlobally += 1;
    }
  };

  const acquireEntry = (entry: SurfaceLifecycleEntry<Descriptor>): Promise<void> => {
    if (!entry.definition.acquire) {
      entry.state = "acquired";
      input.onAcquired?.({
        kind: entry.descriptor.kind,
        surfaceId: entry.descriptor.id,
        resourceIdentity: entry.identity,
        scope: entryScope.get(entry)!,
      });
      enforceRetention();
      return Promise.resolve();
    }
    return Promise.resolve()
      .then(() => {
        if (
          disposed ||
          entry.releaseReason !== null ||
          entries.get(entry.key) !== entry ||
          entry.controller.signal.aborted
        ) {
          return undefined;
        }
        return entry.definition.acquire?.({
          descriptor: entry.descriptor,
          generation: entry.generation,
          scope: entryScope.get(entry)!,
          signal: entry.controller.signal,
        });
      })
      .then((lease) => {
        if (!lease) return;
        entry.lease = lease;
        if (
          disposed ||
          entry.releaseReason !== null ||
          entries.get(entry.key) !== entry ||
          entry.controller.signal.aborted
        ) {
          releaseEntry(entry, entry.releaseReason ?? "replacement");
          return;
        }
        entry.state = "acquired";
        input.onAcquired?.({
          kind: entry.descriptor.kind,
          surfaceId: entry.descriptor.id,
          resourceIdentity: entry.identity,
          scope: entryScope.get(entry)!,
        });
        enforceRetention();
      })
      .catch((cause: unknown) => {
        if (entry.releaseReason !== null || entry.controller.signal.aborted) return;
        entry.state = "failed";
        reportFailure({
          type: "acquisition-failed",
          kind: entry.descriptor.kind,
          surfaceId: entry.descriptor.id,
          resourceIdentity: entry.identity,
          scope: entryScope.get(entry)!,
          generation: entry.generation,
          cause,
        });
      });
  };

  const entryScope = new WeakMap<SurfaceLifecycleEntry<Descriptor>, WorkspaceSurfaceScope>();

  return {
    reconcile: async (reconciliation) => {
      if (disposed) return;
      const nextScopeIdentity = workspaceSurfaceScopeIdentity(reconciliation.scope);
      if (currentScopeIdentity !== null && currentScopeIdentity !== nextScopeIdentity) {
        for (const entry of entries.values()) releaseEntry(entry, "scope-removal");
        evictedIdentities.clear();
      }
      currentScopeIdentity = nextScopeIdentity;
      currentActiveSurfaceId = reconciliation.open ? reconciliation.activeSurfaceId : null;

      const valid = new Map<string, Descriptor>();
      const validSurfaceIds = new Set<string>();
      for (const candidate of reconciliation.descriptors) {
        const descriptor = candidate as Descriptor;
        const definition = definitions.get(descriptor.kind);
        if (!definition) {
          reportFailure({
            type: "unknown-kind",
            kind: descriptor.kind,
            surfaceId: descriptor.id,
          });
          continue;
        }
        if (definition.scope !== reconciliation.scope.type) {
          reportFailure({
            type: "scope-mismatch",
            expectedScope: definition.scope,
            actualScope: reconciliation.scope.type,
            surfaceId: descriptor.id,
          });
          continue;
        }
        const key = descriptorKey(descriptor);
        if (validSurfaceIds.has(descriptor.id)) {
          reportFailure({ type: "duplicate-descriptor", surfaceId: descriptor.id });
          continue;
        }
        validSurfaceIds.add(descriptor.id);
        valid.set(key, descriptor);
      }

      for (const entry of entries.values()) {
        if (!valid.has(entry.key)) releaseEntry(entry, "close");
      }
      for (const key of evictedIdentities.keys()) {
        if (!valid.has(key)) evictedIdentities.delete(key);
      }

      const acquisitions: Promise<void>[] = [];
      for (const [key, descriptor] of valid) {
        const definition = definitions.get(descriptor.kind)!;
        const isActive = reconciliation.open && reconciliation.activeSurfaceId === descriptor.id;
        const wanted =
          definition.lifetime === "active"
            ? isActive
            : definition.lifetime === "while-open"
              ? reconciliation.open
              : true;
        const identity = descriptorIdentity(definition, descriptor);
        const current = entries.get(key);

        if (
          definition.lifetime === "retained" &&
          !isActive &&
          evictedIdentities.get(key) === identity
        ) {
          continue;
        }
        evictedIdentities.delete(key);

        if (!wanted) {
          if (current) {
            releaseEntry(
              current,
              definition.lifetime === "while-open" ? "interruption" : "replacement",
            );
          }
          continue;
        }
        if (current && current.identity === identity) {
          if (isActive) {
            nextActiveSequence += 1;
            current.lastActiveSequence = nextActiveSequence;
          }
          continue;
        }
        if (current) releaseEntry(current, "replacement");

        nextGeneration += 1;
        if (isActive) nextActiveSequence += 1;
        const entry: SurfaceLifecycleEntry<Descriptor> = {
          key,
          definition,
          descriptor,
          identity,
          generation: nextGeneration,
          controller: new AbortController(),
          lease: null,
          releaseReason: null,
          state: "pending",
          lastActiveSequence: isActive ? nextActiveSequence : 0,
        };
        entries.set(key, entry);
        entryScope.set(entry, reconciliation.scope);
        if (definition.lifetime === "retained") {
          enforceRetention();
          if (entries.get(key) !== entry) continue;
        }
        acquisitions.push(acquireEntry(entry));
      }

      await Promise.all(acquisitions);
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      for (const entry of entries.values()) releaseEntry(entry, "owner-disposal");
      evictedIdentities.clear();
    },
  };
}

export interface WorkspaceSurfaceHostProps<Descriptor extends WorkspaceSurfaceDescriptor> {
  readonly definitions: readonly WorkspaceSurfaceDefinition<Descriptor>[];
  readonly state: WorkspaceSurfaceState<Descriptor>;
  readonly scope: WorkspaceSurfaceScope;
  readonly useSheet: boolean;
  readonly renderRegion: (
    presentation: WorkspaceSurfacePresentation,
    content?: ReactNode,
  ) => ReactNode;
  readonly globalRetentionLimit?: number;
  readonly onFailure?: ReportFailure;
  readonly renderFailure?: (failure: WorkspaceSurfaceFailure) => ReactNode;
}

export function reportWorkspaceSurfaceFailure(failure: WorkspaceSurfaceFailure): void {
  console.error("Workspace surface lifecycle failure.", failure);
}

export function renderWorkspaceSurfaceFailure(failure: WorkspaceSurfaceFailure): ReactNode {
  return (
    <div
      role="alert"
      className="flex min-h-0 flex-1 items-center justify-center p-6 text-sm text-muted-foreground"
      data-workspace-surface-failure={failure.type}
    >
      This workspace surface is unavailable. Close it and try opening it again.
    </div>
  );
}

export function WorkspaceSurfaceHost<Descriptor extends WorkspaceSurfaceDescriptor>(
  props: WorkspaceSurfaceHostProps<Descriptor>,
) {
  const [acquisitionFailure, setAcquisitionFailure] = useState<WorkspaceSurfaceFailure | null>(
    null,
  );
  const failureReporterRef = useRef(props.onFailure);
  failureReporterRef.current = props.onFailure;
  const reportFailure = useCallback((failure: WorkspaceSurfaceFailure) => {
    if (failure.type === "acquisition-failed") setAcquisitionFailure(failure);
    (failureReporterRef.current ?? reportWorkspaceSurfaceFailure)(failure);
  }, []);
  const definitions = useMemo(
    () => definitionMap(props.definitions, reportFailure),
    [props.definitions],
  );
  const handleAcquired = useCallback(
    ({
      kind,
      surfaceId,
      resourceIdentity,
      scope,
    }: {
      readonly kind: string;
      readonly surfaceId: string;
      readonly resourceIdentity: string;
      readonly scope: WorkspaceSurfaceScope;
    }) => {
      setAcquisitionFailure((current) =>
        current?.type === "acquisition-failed" &&
        current.kind === kind &&
        current.surfaceId === surfaceId &&
        current.resourceIdentity === resourceIdentity &&
        workspaceSurfaceScopeIdentity(current.scope) === workspaceSurfaceScopeIdentity(scope)
          ? null
          : current,
      );
    },
    [],
  );
  const lifecycleRef = useRef<WorkspaceSurfaceLifecycle | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const previousOpenRef = useRef(false);
  const presentation: WorkspaceSurfacePresentation = props.useSheet ? "sheet" : "inline";

  const activeDescriptor = props.state.isOpen
    ? (props.state.surfaces.find((descriptor) => descriptor.id === props.state.activeSurfaceId) ??
      null)
    : null;
  const activeDefinition = activeDescriptor ? definitions.get(activeDescriptor.kind) : undefined;
  const descriptorFailure = activeDescriptor
    ? activeDefinition
      ? activeDefinition.scope === props.scope.type
        ? null
        : ({
            type: "scope-mismatch",
            expectedScope: activeDefinition.scope,
            actualScope: props.scope.type,
            surfaceId: activeDescriptor.id,
          } satisfies WorkspaceSurfaceFailure)
      : ({
          type: "unknown-kind",
          kind: activeDescriptor.kind,
          surfaceId: activeDescriptor.id,
        } satisfies WorkspaceSurfaceFailure)
    : null;
  const activeResourceIdentity =
    activeDescriptor && activeDefinition
      ? descriptorIdentity(activeDefinition, activeDescriptor)
      : null;
  const activeAcquisitionFailure =
    acquisitionFailure?.type === "acquisition-failed" &&
    activeDescriptor?.id === acquisitionFailure.surfaceId &&
    activeDescriptor.kind === acquisitionFailure.kind &&
    activeResourceIdentity === acquisitionFailure.resourceIdentity &&
    workspaceSurfaceScopeIdentity(props.scope) ===
      workspaceSurfaceScopeIdentity(acquisitionFailure.scope)
      ? acquisitionFailure
      : null;
  const renderFailure = descriptorFailure ?? activeAcquisitionFailure;

  useEffect(() => {
    const lifecycle = createWorkspaceSurfaceLifecycle({
      definitions: props.definitions,
      ...(props.globalRetentionLimit === undefined
        ? {}
        : { globalRetentionLimit: props.globalRetentionLimit }),
      onFailure: reportFailure,
      onAcquired: handleAcquired,
    });
    lifecycleRef.current = lifecycle;
    return () => {
      if (lifecycleRef.current === lifecycle) lifecycleRef.current = null;
      lifecycle.dispose();
    };
  }, [handleAcquired, props.definitions, props.globalRetentionLimit, reportFailure]);

  useEffect(() => {
    void lifecycleRef.current?.reconcile({
      descriptors: props.state.surfaces,
      activeSurfaceId: props.state.activeSurfaceId,
      open: props.state.isOpen,
      scope: props.scope,
    });
  }, [
    props.globalRetentionLimit,
    props.definitions,
    props.scope,
    props.state.activeSurfaceId,
    props.state.isOpen,
    props.state.surfaces,
  ]);

  useLayoutEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = props.state.isOpen;
    if (!wasOpen && props.state.isOpen) {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && !hostRef.current?.contains(focused)) {
        returnFocusRef.current = focused;
      }
      queueMicrotask(() => {
        hostRef.current
          ?.querySelector<HTMLElement>("[data-workspace-surface-primary-focus]")
          ?.focus();
      });
      return;
    }
    if (wasOpen && !props.state.isOpen) {
      const target = returnFocusRef.current;
      queueMicrotask(() => {
        if (target?.isConnected) target.focus();
      });
    }
  }, [presentation, props.state.activeSurfaceId, props.state.isOpen]);

  if (!props.state.isOpen) return null;

  const content = renderFailure
    ? (props.renderFailure ?? renderWorkspaceSurfaceFailure)(renderFailure)
    : activeDescriptor && activeDefinition
      ? activeDefinition.render({
          descriptor: activeDescriptor,
          presentation,
          renderRegion: (content) => props.renderRegion(presentation, content),
          scope: props.scope,
        })
      : props.renderRegion(presentation);

  return (
    <div
      ref={hostRef}
      className="contents"
      data-workspace-surface-host
      data-workspace-surface-kind={activeDescriptor?.kind}
      data-workspace-surface-persistence={activeDefinition?.persistence}
      data-workspace-surface-presentation={presentation}
    >
      {content}
    </div>
  );
}
